import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiCalendar,
    mdiClock,
    mdiCurrencyUsd,
    mdiAlertCircle,
    mdiDanceBallroom,
    mdiTicket,
    mdiSchool,
    mdiChevronDown,
    mdiChevronUp,
    mdiCreditCardClockOutline,
    mdiCreditCardCheckOutline,
    mdiCellphoneCheck,
} from "@mdi/js";
import { toast } from "react-toastify";
import { formatCurrencyFromMinor as formatCurrencyValue } from "../utils/currency";
import { formatZoneFromISO } from "../utils/timezoneLabel";
import { useAuth } from "../contexts/AuthContext";
import AppHeader from "../components/AppHeader";
import StoreBanner from "../components/store-banner";
import { bookingApi, LESSONS_HISTORY_PAGE } from "../api/booking";
import { ticketApi } from "../api/ticket";
import { classApi } from "../api/classes";
import "./MyHub.css";
import "./header.css";
import "./Common.css";
import "./Dashboard.css";

// Preserve the pre-migration empty-state behaviour: missing amount renders "—",
// a real 0 still formats as A$0.00.
const formatCurrency = (amount, currency) =>
    amount == null ? "—" : formatCurrencyValue(amount, currency);

const STATUS_LABELS = {
    pending: "Awaiting approval",
    confirmed: "Enrolled",
    paid: "Paid",
    completed: "Completed",
    cancelled: "Cancelled",
    rejected: "Rejected",
    waitlisted: "Waitlisted",
};

const statusPillClass = status => `status-pill status-pill--${(status || "cancelled").replace(/_/g, "-")}`;

const formatDate = isoDate => {
    if (!isoDate) return "—";
    return new Date(isoDate).toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
};

// Ticket event date rendered in the EVENT's own timezone + the canonical indicator
// "(GMT+12 Auckland)" — matches the landing/purchase/mobile/email surfaces. A late-night
// event can fall on a different calendar day in the viewer's zone, so the event tz is correct.
const formatTicketDate = (isoDate, endIso, tz) => {
    if (!isoDate) return "—";
    const zone = tz || "UTC";
    const dateStr = new Date(isoDate).toLocaleDateString("en-AU", {
        weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: zone,
    });
    const timeStr = (iso) => new Date(iso).toLocaleTimeString("en-AU", {
        hour: "numeric", minute: "2-digit", timeZone: zone,
    });
    const dayKey = (iso) => new Date(iso).toLocaleDateString("en-AU", { timeZone: zone });
    let core = `${dateStr}, ${timeStr(isoDate)}`;
    if (endIso) {
        // Same-day end → time only; multi-day → append the end day+time.
        core += dayKey(isoDate) === dayKey(endIso)
            ? ` - ${timeStr(endIso)}`
            : ` - ${new Date(endIso).toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: zone })}, ${timeStr(endIso)}`;
    }
    const label = tz ? formatZoneFromISO(tz, isoDate) : "";
    return label ? `${core} (${label})` : core;
};

const isPastDate = isoDate => isoDate && new Date(isoDate) < new Date();

const ACTIVE_STATUSES = ["confirmed", "unconfirmed", "waitlisted", "pending"];

/**
 * MyHub — Student/member activity hub.
 * Three accordion sections: Bookings (private lessons), Tickets, Enrolled Classes.
 */
const MyHub = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Accordion state — null = all closed; set after data loads
    const [openSection, setOpenSection] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    // Auto-open runs ONCE after data loads. Without this guard, closing a section
    // (openSection → null) re-triggers the auto-open effect and snaps it back open.
    const hasAutoOpened = useRef(false);

    // Bookings section
    const [bookings, setBookings] = useState([]);
    const [bookingsError, setBookingsError] = useState("");
    const [showBookingHistory, setShowBookingHistory] = useState(false);
    // Lesson history is paged server-side (scope=lessons). Offset counts history rows
    // already loaded; the active set and any unread-bearing history always come back
    // on every page, which is why pages are merged by id rather than appended.
    const [bookingHistoryOffset, setBookingHistoryOffset] = useState(0);
    const [bookingHistoryHasMore, setBookingHistoryHasMore] = useState(false);
    const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);

    // Tickets section
    const [tickets, setTickets] = useState([]);
    const [ticketsError, setTicketsError] = useState("");
    const [showTicketHistory, setShowTicketHistory] = useState(false);

    // Enrolments section
    const [enrolments, setEnrolments] = useState([]);
    const [enrolmentsError, setEnrolmentsError] = useState("");
    const [showEnrolmentHistory, setShowEnrolmentHistory] = useState(false);
    // Fetch all sections on mount
    useEffect(() => {
        if (!user || dataLoaded) return;
        const loadAll = async () => {
            const [bRes, tRes, eRes] = await Promise.allSettled([
                bookingApi.getStudentLessons(),
                ticketApi.getMyTickets(),
                classApi.getStudentEnrolments(),
            ]);

            if (bRes.status === "fulfilled") {
                setBookings(bRes.value.bookings || []);
                setBookingHistoryHasMore(bRes.value.hasMore);
            } else {
                setBookingsError("Failed to load bookings.");
            }

            if (tRes.status === "fulfilled") {
                setTickets(tRes.value || []);
            } else {
                setTicketsError("Failed to load tickets.");
            }

            if (eRes.status === "fulfilled") {
                console.log("[MyHub] Enrolments:", eRes.value);
                setEnrolments(eRes.value || []);
            } else {
                setEnrolmentsError("Failed to load enrolments.");
            }

            setDataLoaded(true);
        };
        loadAll();
    }, [user, dataLoaded]);

    // Auto-open the first section that has active records — ONCE, after data loads.
    // The hasAutoOpened ref ensures this never re-fires when the user later closes a
    // section (which would set openSection back to null and snap the section open again).
    useEffect(() => {
        if (!dataLoaded || hasAutoOpened.current) return;
        hasAutoOpened.current = true;
        const upcomingB = bookings.filter(
            b => !isPastDate(b.bookingDate) && b.status !== "cancelled" && b.status !== "rejected"
        );
        const activeE = enrolments.filter(e => ACTIVE_STATUSES.includes(e.status) && !isPastDate(e.termEndDate));
        const upcomingT = tickets.filter(t => !isPastDate(t.eventDate));
        if (upcomingB.length > 0) setOpenSection("bookings");
        else if (activeE.length > 0) setOpenSection("classes");
        else if (upcomingT.length > 0) setOpenSection("tickets");
    }, [dataLoaded, bookings, enrolments, tickets]);

    const toggleSection = key => {
        setOpenSection(openSection === key ? null : key);
    };

    // "Show more" under the lesson history. The page repeats the active and
    // unread-bearing rows, so merge by id and re-sort newest first (the same order
    // the server returns), rather than appending the page to the end.
    const loadMoreBookingHistory = async () => {
        if (loadingMoreHistory || !bookingHistoryHasMore) return;
        setLoadingMoreHistory(true);
        const nextOffset = bookingHistoryOffset + LESSONS_HISTORY_PAGE;
        try {
            const page = await bookingApi.getStudentLessons({ offset: nextOffset });
            setBookings(prev => {
                const byId = new Map(prev.map(b => [b.id, b]));
                page.bookings.forEach(b => byId.set(b.id, b));
                return [...byId.values()].sort(
                    (a, b) => new Date(b.bookingDate) - new Date(a.bookingDate)
                );
            });
            setBookingHistoryOffset(nextOffset);
            setBookingHistoryHasMore(page.hasMore);
        } catch (err) {
            toast.error("Could not load more history.");
        } finally {
            setLoadingMoreHistory(false);
        }
    };

    const upcomingBookings = bookings.filter(
        b => !isPastDate(b.bookingDate) && b.status !== "cancelled" && b.status !== "rejected"
    );
    const pastBookings = bookings.filter(
        b => isPastDate(b.bookingDate) || b.status === "cancelled" || b.status === "rejected"
    );

    const upcomingTickets = tickets.filter(t => !isPastDate(t.eventDate));
    const pastTickets = tickets.filter(t => isPastDate(t.eventDate));

    const isActiveEnrolment = e => ACTIVE_STATUSES.includes(e.status) && !isPastDate(e.termEndDate);
    const activeEnrolments = enrolments.filter(isActiveEnrolment);
    const pastEnrolments = enrolments.filter(e => !isActiveEnrolment(e));

    const handleEnrolmentConfirmed = updatedEnrolment => {
        setEnrolments(prev => prev.map(e => (e.id === updatedEnrolment.id ? updatedEnrolment : e)));
    };

    const renderSectionHeader = (key, icon, label, count) => (
        <button
            className="my-hub-accordion-header"
            onClick={() => toggleSection(key)}
            aria-expanded={openSection === key}>
            <div className="my-hub-accordion-title">
                <Icon path={icon} size={0.9} color="var(--dl-color-purple-default)" />
                <span>{label}</span>
                {count > 0 && <span className="my-hub-count-badge">{count}</span>}
            </div>
            <Icon
                path={openSection === key ? mdiChevronUp : mdiChevronDown}
                size={0.9}
                color="var(--dl-color-medium)"
            />
        </button>
    );

    const renderError = msg => (
        <div className="my-hub-error">
            <Icon path={mdiAlertCircle} size={0.8} color="var(--dl-color-warning-red)" />
            {msg}
        </div>
    );

    return (
        <div className="dashboard-container org-console my-hub-page">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: "760px" }}>
                    <div className="sub-portal-header-card" style={{ padding: "16px 20px", marginBottom: "8px" }}>
                        <h1 className="sub-portal-header-title" style={{ fontSize: "22px", margin: 0 }}>
                            My Activity
                        </h1>
                        <p
                            style={{
                                color: "var(--dl-color-medium)",
                                margin: "4px 0 0",
                                fontSize: "14px",
                                textAlign: "center",
                            }}>
                            Your bookings, tickets and class enrolments
                        </p>
                    </div>

                    <div className="content-card">
                        {user?.accountDetails?.firstName && (
                            <p
                                style={{
                                    margin: "0 0 12px",
                                    fontSize: "15px",
                                    color: "var(--dl-color-purple-default)",
                                }}>
                                Welcome, <strong>{user.accountDetails.firstName}</strong>
                            </p>
                        )}
                        {/* Accordion: My Bookings */}
                        <div className="my-hub-accordion">
                            {renderSectionHeader("bookings", mdiDanceBallroom, "My Bookings", upcomingBookings.length)}
                            {openSection === "bookings" && (
                                <div className="my-hub-accordion-body">
                                    {!dataLoaded && <p className="my-hub-loading">Loading…</p>}
                                    {bookingsError && renderError(bookingsError)}
                                    {dataLoaded && !bookingsError && upcomingBookings.length === 0 && (
                                        <p className="my-hub-empty">
                                            No upcoming bookings. <Link to="/instructors">Find an instructor</Link>
                                        </p>
                                    )}
                                    {upcomingBookings.map(b => (
                                        <BookingRow key={b.id} booking={b} navigate={navigate} />
                                    ))}
                                    {pastBookings.length > 0 && (
                                        <>
                                            <button
                                                className="my-hub-history-toggle"
                                                onClick={() => setShowBookingHistory(!showBookingHistory)}>
                                                {showBookingHistory ? "Hide history" : "View history"}
                                            </button>
                                            {showBookingHistory && (
                                                <>
                                                    {pastBookings.map(b => (
                                                        <BookingRow key={b.id} booking={b} navigate={navigate} past />
                                                    ))}
                                                    {bookingHistoryHasMore && (
                                                        <button
                                                            className="my-hub-history-toggle"
                                                            onClick={loadMoreBookingHistory}
                                                            disabled={loadingMoreHistory}>
                                                            {loadingMoreHistory ? "Loading..." : "Show more"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Accordion: My Tickets */}
                        <div className="my-hub-accordion">
                            {renderSectionHeader("tickets", mdiTicket, "My Tickets", upcomingTickets.length)}
                            {openSection === "tickets" && (
                                <div className="my-hub-accordion-body">
                                    {!dataLoaded && <p className="my-hub-loading">Loading…</p>}
                                    {ticketsError && renderError(ticketsError)}
                                    {dataLoaded && !ticketsError && upcomingTickets.length === 0 && (
                                        <p className="my-hub-empty">No upcoming tickets.</p>
                                    )}
                                    {upcomingTickets.map(t => (
                                        <TicketRow key={t.id} ticket={t} />
                                    ))}
                                    {pastTickets.length > 0 && (
                                        <>
                                            <button
                                                className="my-hub-history-toggle"
                                                onClick={() => setShowTicketHistory(!showTicketHistory)}>
                                                {showTicketHistory
                                                    ? "Hide history"
                                                    : `View history (${pastTickets.length})`}
                                            </button>
                                            {showTicketHistory &&
                                                pastTickets.map(t => <TicketRow key={t.id} ticket={t} past />)}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Accordion: My Classes */}
                        <div className="my-hub-accordion">
                            {renderSectionHeader("classes", mdiSchool, "My Enrolled Classes", activeEnrolments.length)}
                            {openSection === "classes" && (
                                <div className="my-hub-accordion-body">
                                    {!dataLoaded && <p className="my-hub-loading">Loading…</p>}
                                    {enrolmentsError && renderError(enrolmentsError)}
                                    {dataLoaded && !enrolmentsError && activeEnrolments.length === 0 && (
                                        <p className="my-hub-empty">You are not enrolled in any classes.</p>
                                    )}
                                    {activeEnrolments.some(e => e.paymentStatus === "awaiting_payment") && (
                                        <p className="my-hub-enrolment-hint">
                                            If you have multiple enrolments with one Pay Now button, your school has set
                                            up a combined payment package. Click Pay Now to see what is included. For
                                            any queries contact your school.
                                        </p>
                                    )}
                                    {(() => {
                                        const seenTokens = new Set();
                                        return activeEnrolments.map(e => {
                                            const isFirstForToken = e.paymentToken && !seenTokens.has(e.paymentToken);
                                            if (e.paymentToken) seenTokens.add(e.paymentToken);
                                            return (
                                                <EnrolmentRow
                                                    key={e.id}
                                                    enrolment={e}
                                                    onConfirmed={handleEnrolmentConfirmed}
                                                    showPayNow={isFirstForToken}
                                                />
                                            );
                                        });
                                    })()}
                                    {pastEnrolments.length > 0 && (
                                        <>
                                            <button
                                                className="my-hub-history-toggle"
                                                onClick={() => setShowEnrolmentHistory(!showEnrolmentHistory)}>
                                                {showEnrolmentHistory
                                                    ? "Hide history"
                                                    : `View history (${pastEnrolments.length})`}
                                            </button>
                                            {showEnrolmentHistory &&
                                                pastEnrolments.map(e => <EnrolmentRow key={e.id} enrolment={e} past />)}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* end content-card */}

                    <div className="my-hub-app-cta-banner">
                        <div className="my-hub-app-cta-icon">
                            <Icon path={mdiCellphoneCheck} size={0.95} />
                        </div>
                        <p className="my-hub-app-cta-heading">Get more from the app</p>
                        <p className="my-hub-app-cta-body">
                            Browse events, book your favourite instructors, buy tickets and get notifications - all in
                            the <strong>Dance Local&#233;</strong> App.
                        </p>
                        <div className="my-hub-app-cta-store-banner">
                            <StoreBanner />
                        </div>

                        {/* Subtle, edge-case upgrade entry — consumers only (no org). Existing
                            org owners never see it. A re-attempt by someone who already has an org
                            is 409-guarded server-side and surfaced as a toast on the target page. */}
                        {!user?.organisationId && (
                            <p className="my-hub-upgrade-link">
                                Run a dance school, teach privately, or want to sell tickets?{" "}
                                <span
                                    className="my-hub-upgrade-link-action"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate("/register/organisation")}
                                    onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate("/register/organisation")}>
                                    Upgrade your account
                                </span>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const BookingRow = ({ booking, navigate, past }) => {
    const paymentStatus = booking.pricing?.paymentStatus;
    // A cancelled booking can never be paid, so it never shows a pay cue.
    const paymentDue = booking.status !== "cancelled"
        && paymentStatus === "pending"
        && booking.pricing?.paymentTiming === "before-lesson";
    // Prepaid bookings stay pending until paid; there is no approval step.
    const statusLabel = booking.status === "pending" && paymentDue
        ? "Awaiting payment"
        : STATUS_LABELS[booking.status] || booking.status;

    // Students see HOW they paid ("Paid by cash"), matching mobile My Lessons + the class
    // rows above. 'stripe' renders as "card"; the instructor reconcile axis is never shown.
    const paidMethod = (() => {
        const m = booking.effectivePricing?.paidBy;
        const label = m === "platform_credit" ? "credit"
            : m === "bank_transfer" ? "bank transfer"
            : m === "eftpos" ? "EFTPOS"
            : m === "cash" ? "cash"
            : m === "stripe" ? "card"
            : null;
        return label ? `Paid by ${label}` : "Paid";
    })();

    return (
        <div className={`my-hub-row ${past ? "past" : ""}`}>
            <div className="my-hub-row-main">
                <span className="my-hub-row-title">{booking.instructorName || "Instructor"}</span>
                <div className="my-hub-row-badges">
                    <span className={statusPillClass(booking.status)}>{statusLabel}</span>
                    {paymentStatus === "paid" && (
                        <>
                            <span className="status-pill status-pill--paid">{paidMethod}</span>
                            <span className="my-hub-payment-icon">
                                <Icon path={mdiCreditCardCheckOutline} size={1.1} color="var(--dl-color-greentick)" />
                            </span>
                        </>
                    )}
                    {paymentDue && (
                        <>
                            <span className="status-pill status-pill--pending-payment">Payment due</span>
                            <span className="my-hub-payment-icon">
                                <Icon path={mdiCreditCardClockOutline} size={1.1} color="var(--dl-color-needs-action)" />
                            </span>
                        </>
                    )}
                </div>
            </div>
            <div className="my-hub-row-details">
                <span>
                    <Icon path={mdiCalendar} size={0.7} color="var(--dl-color-purple-default)" />{" "}
                    {formatDate(booking.bookingDate)}
                </span>
                {booking.timeSlot && (
                    <span>
                        <Icon path={mdiClock} size={0.7} color="var(--dl-color-purple-default)" />{" "}
                        {booking.timeSlot.startTime} – {booking.timeSlot.endTime}
                    </span>
                )}
                {booking.pricing?.amount && (
                    <span>
                        <Icon path={mdiCurrencyUsd} size={0.7} color="var(--dl-color-purple-default)" />{" "}
                        {formatCurrency(booking.pricing.amount, booking.pricing.currency)}
                    </span>
                )}
                {paymentDue && (
                    <button
                        className="my-hub-pay-btn"
                        onClick={() =>
                            navigate("/booking-payment-guest", {
                                state: {
                                    bookingId: booking.id,
                                    bookingPreview: {
                                        instructorName: booking.instructorName,
                                        instructorId: booking.instructorId,
                                        dateDisplay: formatDate(booking.bookingDate),
                                        timeSlot: booking.timeSlot,
                                        studentInfo: booking.studentInfo,
                                        pricing: booking.pricing,
                                    },
                                },
                            })
                        }>
                        Pay Now
                    </button>
                )}
            </div>
        </div>
    );
};

const TicketRow = ({ ticket, past }) => (
    <div className={`my-hub-row ${past ? "past" : ""}`}>
        <div className="my-hub-row-main">
            <span className="my-hub-row-title">{ticket.eventName || "Event"}</span>
            {ticket.ticketNumber && <span className="my-hub-row-sub">ID: {ticket.ticketNumber}</span>}
        </div>
        <div className="my-hub-row-details">
            {ticket.eventDate && (
                <span>
                    <Icon path={mdiCalendar} size={0.7} color="var(--dl-color-purple-default)" />{" "}
                    {formatTicketDate(ticket.eventDate, ticket.eventEndDate, ticket.timezone)}
                </span>
            )}
            {ticket.ticketType && (
                <span>
                    <Icon path={mdiTicket} size={0.7} color="var(--dl-color-purple-default)" /> {ticket.ticketType}
                </span>
            )}
        </div>
    </div>
);

const EnrolmentRow = ({ enrolment, past, onConfirmed, showPayNow: showPayNowProp }) => {
    const navigate = useNavigate();
    const [isGenerating, setIsGenerating] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const isUnconfirmed = enrolment.status === "unconfirmed";
    const isPaid = enrolment.paymentStatus === "paid";
    const isPending = enrolment.paymentStatus === "awaiting_payment";
    const showPayNow = !past && !isUnconfirmed && isPending && showPayNowProp !== false;

    // Students see HOW they paid ("Paid by cash"), mirroring the My Lessons booking view.
    // 'stripe' renders as "card"; the instructor reconcile axis is never shown here.
    const paidMethod = (() => {
        const m = enrolment.paymentMethod;
        const label = m === "bank_transfer" ? "bank transfer"
            : m === "eftpos" ? "EFTPOS"
            : m === "cash" ? "cash"
            : m === "stripe" ? "card"
            : null;
        return label ? `Paid by ${label}` : "Paid";
    })();

    const handlePayNow = async () => {
        setIsGenerating(true);
        try {
            const response = await classApi.studentGenerateQrToken(enrolment.id);
            if (response.ok && response.data?.qrToken) {
                navigate(`/class-payment/${response.data.qrToken}`);
            } else {
                toast.error(response.data?.message || "Could not generate payment link. Please try again.");
            }
        } catch {
            toast.error("Could not generate payment link. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleConfirm = async () => {
        setIsConfirming(true);
        try {
            const response = await classApi.confirmEnrolment(enrolment.id);
            if (response.ok && response.data?.enrolment) {
                onConfirmed?.(response.data.enrolment);
                toast.success("Enrolment confirmed!");
            } else {
                toast.error(response.data?.message || "Could not confirm enrolment. Please try again.");
            }
        } catch {
            toast.error("Could not confirm enrolment. Please try again.");
        } finally {
            setIsConfirming(false);
        }
    };

    const titleParts = [enrolment.className || enrolment.courseName, enrolment.termName, enrolment.orgName].filter(
        Boolean
    );

    return (
        <div className={`my-hub-row ${past ? "past" : ""}`}>
            <div className="my-hub-enrolment-row-inner">
                <div className="my-hub-enrolment-info">
                    <span className="my-hub-row-title">{titleParts.join(" — ") || "Class"}</span>
                    {isUnconfirmed && (
                        <span className="my-hub-enrolment-invite-hint">
                            You've been invited to re-enrol in this class
                        </span>
                    )}
                </div>

                <div className="my-hub-enrolment-right">
                    {isUnconfirmed && !past && (
                        <>
                            <span className="my-hub-enrolment-pill unconfirmed">Re-enrol</span>
                            <button
                                className="my-hub-enrolment-confirm-btn"
                                onClick={handleConfirm}
                                disabled={isConfirming}>
                                {isConfirming ? "Confirming..." : "Confirm"}
                            </button>
                        </>
                    )}

                    {!isUnconfirmed && isPaid && (
                        <div className="my-hub-enrolment-status">
                            <span className="status-pill status-pill--paid">{paidMethod}</span>
                            <span className="my-hub-payment-icon">
                                <Icon path={mdiCreditCardCheckOutline} size={1.1} color="var(--dl-color-greentick)" />
                            </span>
                        </div>
                    )}

                    {showPayNow && (
                        <div className="my-hub-enrolment-status">
                            <span className="status-pill status-pill--pending-payment">Payment due</span>
                            <span className="my-hub-payment-icon">
                                <Icon path={mdiCreditCardClockOutline} size={1.1} color="var(--dl-color-needs-action)" />
                            </span>
                            <button className="my-hub-pay-btn" onClick={handlePayNow} disabled={isGenerating}>
                                {isGenerating ? "Loading..." : "Pay Now"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyHub;
